import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

/**
 * Accent-palette switcher — a temporary auditioning tool while Jorge
 * decides on the brand accent. Sets `data-palette` on <html>; the CSS in
 * index.css swaps the accent (and, for "mono", the spectra) tokens.
 *
 * Mirrors the theme provider: synchronous first read + a no-flash inline
 * script in index.html keep the chosen palette from flashing on load.
 * "terracotta" is the default and clears the attribute.
 */

const STORAGE_KEY = "lingua.accent";

export type AccentPalette =
  | "forest"
  | "emerald"
  | "sky"
  | "cyan"
  | "blue"
  | "slate";

const DEFAULT_PALETTE: AccentPalette = "blue";

// `swatch` is the light-mode accent hex, shown as the chip dot in the
// picker (the data-palette CSS is scoped to <html>, so a chip can't read
// the token directly — a literal keeps the preview honest and simple).
export const ACCENT_PALETTES: {
  value: AccentPalette;
  label: string;
  swatch: string;
}[] = [
  { value: "blue", label: "Ink blue", swatch: "#1d4ed8" },
  { value: "forest", label: "Forest green", swatch: "#15803d" },
  { value: "emerald", label: "Emerald", swatch: "#059669" },
  { value: "sky", label: "Sky", swatch: "#0284c7" },
  { value: "cyan", label: "Cyan", swatch: "#0891b2" },
  { value: "slate", label: "Slate", swatch: "#475569" },
];

function isAccentPalette(v: string): v is AccentPalette {
  return ACCENT_PALETTES.some((p) => p.value === v);
}

export function readStoredAccent(): AccentPalette {
  if (typeof window === "undefined") return DEFAULT_PALETTE;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw && isAccentPalette(raw)) return raw;
  } catch {
    // restricted storage — fall through
  }
  return DEFAULT_PALETTE;
}

function applyAccent(p: AccentPalette) {
  if (typeof document === "undefined") return;
  // The default is the base defined in @theme — no attribute needed.
  if (p === DEFAULT_PALETTE) {
    delete document.documentElement.dataset.palette;
  } else {
    document.documentElement.dataset.palette = p;
  }
}

type AccentValue = {
  palette: AccentPalette;
  setPalette: (p: AccentPalette) => void;
};

const AccentContext = createContext<AccentValue | null>(null);

export function AccentPaletteProvider({ children }: { children: ReactNode }) {
  const [palette, setPaletteState] = useState<AccentPalette>(() =>
    readStoredAccent(),
  );

  const setPalette = useCallback((p: AccentPalette) => {
    setPaletteState(p);
    applyAccent(p);
    try {
      window.localStorage.setItem(STORAGE_KEY, p);
    } catch {
      // session-only if storage is blocked
    }
  }, []);

  // Re-assert on mount in case the inline script and React disagree.
  useEffect(() => {
    applyAccent(palette);
  }, [palette]);

  const value = useMemo(() => ({ palette, setPalette }), [palette, setPalette]);

  return (
    <AccentContext.Provider value={value}>{children}</AccentContext.Provider>
  );
}

export function useAccentPalette(): AccentValue {
  const ctx = useContext(AccentContext);
  if (!ctx) {
    throw new Error(
      "useAccentPalette must be called inside <AccentPaletteProvider>",
    );
  }
  return ctx;
}
